import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaCheck, FaTimes, FaReply, FaTrash, FaSearch, FaEye } from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [showReplyModal, setShowReplyModal] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await apiService.getContacts();
      setContacts(response.data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await apiService.deleteContact(id);
        toast.success('Message deleted successfully');
        fetchContacts();
      } catch (error) {
        toast.error('Failed to delete message');
      }
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await apiService.markContactRead(id);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to mark message as read');
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    try {
      await apiService.replyToContact(selectedContact.id, { message: replyText });
      toast.success('Reply sent successfully');
      setShowReplyModal(false);
      setReplyText('');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Contact Messages</h1>
          <p className="text-gray-400 mt-1">Manage messages from your contact form</p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Messages List */}
      <div className="space-y-4">
        {filteredContacts.map((contact) => (
          <motion.div
            key={contact.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`glass rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 ${
              !contact.is_read ? 'border-primary-500/30' : ''
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center text-primary-400">
                    <FaEnvelope />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{contact.full_name}</h3>
                    <p className="text-gray-400 text-sm">{contact.email}</p>
                  </div>
                  {!contact.is_read && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-primary-500/20 text-primary-400">
                      New
                    </span>
                  )}
                  {contact.replied && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400">
                      Replied
                    </span>
                  )}
                </div>

                {contact.subject && (
                  <p className="text-white font-medium mb-1">{contact.subject}</p>
                )}
                <p className="text-gray-300 text-sm line-clamp-2">{contact.message}</p>

                <p className="text-xs text-gray-500 mt-2">
                  {new Date(contact.created_at).toLocaleString()}
                </p>
              </div>

              <div className="flex gap-2">
                {!contact.is_read && (
                  <button
                    onClick={() => handleMarkRead(contact.id)}
                    className="p-2 rounded-lg bg-primary-500/20 hover:bg-primary-500/30 transition-all duration-300 text-primary-400"
                    title="Mark as read"
                  >
                    <FaEye size={14} />
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedContact(contact);
                    setShowReplyModal(true);
                  }}
                  className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-all duration-300 text-green-400"
                  title="Reply"
                >
                  <FaReply size={14} />
                </button>
                <button
                  onClick={() => handleDelete(contact.id)}
                  className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 transition-all duration-300 text-gray-400 hover:text-red-400"
                >
                  <FaTrash size={14} />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredContacts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400">No messages found.</p>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && selectedContact && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-dark-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Reply to Message</h2>
              <button
                onClick={() => setShowReplyModal(false)}
                className="p-2 rounded-lg hover:bg-white/10 transition-all duration-300 text-gray-400"
              >
                ✕
              </button>
            </div>

            <div className="mb-4 p-4 glass rounded-xl">
              <p className="text-sm text-gray-400">From: {selectedContact.full_name} ({selectedContact.email})</p>
              {selectedContact.subject && (
                <p className="text-sm text-gray-400 mt-1">Subject: {selectedContact.subject}</p>
              )}
              <p className="text-white mt-2">{selectedContact.message}</p>
            </div>

            <form onSubmit={handleReply} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Your Reply *
                </label>
                <textarea
                  required
                  rows="4"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Type your reply here..."
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 flex items-center gap-2"
                >
                  <FaReply />
                  Send Reply
                </button>
                <button
                  type="button"
                  onClick={() => setShowReplyModal(false)}
                  className="px-6 py-2 border border-white/10 rounded-lg font-medium text-gray-400 hover:bg-white/5 transition-all duration-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminContacts;